/**
 * CaseStudyChart — compact, data-driven growth graphic for case-study cards.
 *
 * Renders just the chart (axis, gridlines, line/bars, datapoints) — the card
 * already supplies the big metric, label and before/after, so this is pure
 * visual proof of the growth. Pure inline SVG: no chart lib, negligible weight,
 * scales to its container width.
 *
 * Keyed by case-study `slug`. Only the seven real / vetted client stories have
 * a config; every other slug renders nothing, so the component is safe to drop
 * into any card grid (Home, service pages, industry pages) — it shows a graph
 * only where the underlying numbers are real.
 *
 * Endpoints and totals are the real figures; the in-between curve shape is an
 * eased interpolation (illustrative ramp), consistent across the set.
 *
 * Palette matches the site: #57d7e2 cyan accent on the dark #111 card.
 */
import React from "react";

const ACCENT = "#57d7e2";
const BRIGHT = "#9ff2f6";
const GRID = "rgba(255,255,255,0.08)";
const AXIS = "rgba(255,255,255,0.15)";
const TICK = "rgba(255,255,255,0.35)";
const ff = "'Barlow', sans-serif";

// shared viewBox geometry
const VB_W = 300;
const VB_H = 132;
const TOP = 16;
const BASE = 110; // x-axis baseline
const L = 10; // plot left
const R = 290; // plot right

type LineChart = {
  type: "line";
  from: number;
  to: number;
  ceiling: number;
  months: number;
  fromLabel?: string;
  toLabel: string;
  ease?: number;
};
type TwinLine = {
  type: "twinline";
  ceiling: number;
  months: number;
  upper: { to: number; label: string };
  lower: { to: number; label: string };
  ease?: number;
};
type Bars = {
  type: "bars";
  ceiling: number;
  bars: { label: string; value: number; muted?: boolean }[];
};
type ChartConfig = LineChart | TwinLine | Bars;

// Endpoints/totals are real; curve shape is an eased ramp (ease > 1 accelerates).
const CHARTS: Record<string, ChartConfig> = {
  "industrial-humidification-manufacturer": {
    type: "bars",
    ceiling: 1.8,
    bars: [
      { label: "$90K", value: 0.09, muted: true },
      { label: "$1.62m", value: 1.62 },
    ],
  },
  "solar-installer": {
    type: "twinline",
    ceiling: 1.6,
    months: 10,
    upper: { to: 1.5, label: "$1.5m" },
    lower: { to: 1.1, label: "$1.1m" },
    ease: 1.5,
  },
  "branding-agency": {
    type: "bars",
    ceiling: 1.4,
    bars: [
      { label: "$120K", value: 0.12, muted: true },
      { label: "$420K", value: 0.42 },
      { label: "$1.35m", value: 1.35 },
    ],
  },
  "storage-company": {
    type: "line",
    from: 150,
    to: 920,
    ceiling: 1000,
    months: 14,
    fromLabel: "150",
    toLabel: "920",
    ease: 1.5,
  },
  "healthcare-clinic": {
    type: "line",
    from: 0,
    to: 288,
    ceiling: 320,
    months: 24,
    toLabel: "$288k",
    ease: 2,
  },
  "invoice-management-saas": {
    type: "line",
    from: 0,
    to: 264,
    ceiling: 300,
    months: 12,
    toLabel: "$264k",
    ease: 1.9,
  },
  "private-equity-saas": {
    type: "line",
    from: 0,
    to: 100,
    ceiling: 120,
    months: 24,
    toLabel: "$100k",
    ease: 2,
  },
  "b2b-saas-platform": {
    type: "line",
    from: 0,
    to: 2.4,
    ceiling: 2.6,
    months: 12,
    toLabel: "$2.4m",
    ease: 1.9,
  },
  "ai-visa-planning-platform": {
    type: "line",
    from: 0,
    to: 24,
    ceiling: 28,
    months: 8, // 8 weeks — granularity for a smooth ramp; axis is unlabelled
    fromLabel: "DR 0",
    toLabel: "DR 24",
    ease: 1.9,
  },
};

/** True when a slug has a real, vetted chart config (so callers can render a
 *  proof panel only where the underlying numbers exist). */
export function hasCaseStudyChart(slug: string): boolean {
  return slug in CHARTS;
}

const x = (m: number, months: number) => L + (m / months) * (R - L);
const y = (v: number, ceiling: number) => BASE - (v / ceiling) * (BASE - TOP);

function ramp(from: number, to: number, months: number, ease: number): number[] {
  const pts: number[] = [];
  for (let m = 0; m <= months; m++) {
    const t = m / months;
    pts.push(from + (to - from) * Math.pow(t, ease));
  }
  return pts;
}

function toPath(vals: number[], months: number, ceiling: number): string {
  return vals.map((v, m) => `${m === 0 ? "M" : "L"}${x(m, months).toFixed(1)} ${y(v, ceiling).toFixed(1)}`).join(" ");
}

function LineChartBody({ c, gid }: { c: LineChart; gid: string }) {
  const ease = c.ease ?? 1.7;
  const vals = ramp(c.from, c.to, c.months, ease);
  const line = toPath(vals, c.months, c.ceiling);
  const area = `${line} L${R} ${BASE} L${L} ${BASE} Z`;
  const step = Math.max(1, Math.round(c.months / 6));
  const dots: number[] = [];
  for (let m = step; m < c.months; m += step) dots.push(m);
  return (
    <>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {dots.map((m) => (
        <circle key={m} cx={x(m, c.months)} cy={y(vals[m], c.ceiling)} r={2.6} fill="#111" stroke={ACCENT} strokeWidth={1.3} />
      ))}
      <circle cx={x(0, c.months)} cy={y(c.from, c.ceiling)} r={3.2} fill="#111" stroke={ACCENT} strokeWidth={1.6} />
      <circle cx={x(c.months, c.months)} cy={y(c.to, c.ceiling)} r={6} fill={ACCENT} opacity={0.22} />
      <circle cx={x(c.months, c.months)} cy={y(c.to, c.ceiling)} r={3.6} fill={BRIGHT} />
      {c.fromLabel && (
        <text x={x(0, c.months)} y={y(c.from, c.ceiling) + 14} fontSize={10} fontFamily={ff} fill={TICK} textAnchor="middle">{c.fromLabel}</text>
      )}
      <text x={x(c.months, c.months)} y={y(c.to, c.ceiling) - 8} fontSize={11} fontFamily={ff} fontWeight={700} fill={BRIGHT} textAnchor="end">{c.toLabel}</text>
    </>
  );
}

function TwinLineBody({ c, gid }: { c: TwinLine; gid: string }) {
  const ease = c.ease ?? 1.6;
  const up = ramp(0, c.upper.to, c.months, ease);
  const lo = ramp(0, c.lower.to, c.months, ease);
  const loArea = `${toPath(lo, c.months, c.ceiling)} L${R} ${BASE} L${L} ${BASE} Z`;
  return (
    <>
      <path d={loArea} fill={`url(#${gid})`} />
      <path d={toPath(up, c.months, c.ceiling)} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="2 5" opacity={0.75} />
      <path d={toPath(lo, c.months, c.ceiling)} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(c.months, c.months)} cy={y(c.upper.to, c.ceiling)} r={3.4} fill={ACCENT} />
      <circle cx={x(c.months, c.months)} cy={y(c.lower.to, c.ceiling)} r={6} fill={ACCENT} opacity={0.22} />
      <circle cx={x(c.months, c.months)} cy={y(c.lower.to, c.ceiling)} r={3.6} fill={BRIGHT} />
      <text x={x(c.months, c.months)} y={y(c.upper.to, c.ceiling) - 7} fontSize={10} fontFamily={ff} fontWeight={700} fill={ACCENT} textAnchor="end">{c.upper.label}</text>
      <text x={x(c.months, c.months)} y={y(c.lower.to, c.ceiling) + 13} fontSize={10} fontFamily={ff} fontWeight={600} fill={BRIGHT} textAnchor="end">{c.lower.label}</text>
    </>
  );
}

function BarsBody({ c, gid }: { c: Bars; gid: string }) {
  const W = 46;
  const n = c.bars.length;
  const centers = c.bars.map((_, i) => L + ((i + 0.5) / n) * (R - L));
  return (
    <>
      {c.bars.map((b, i) => {
        const bh = (b.value / c.ceiling) * (BASE - TOP);
        const cx = centers[i];
        return (
          <g key={i}>
            <rect
              x={cx - W / 2}
              y={BASE - bh}
              width={W}
              height={bh}
              rx={2}
              fill={b.muted ? "rgba(87,215,226,0.18)" : i === n - 1 ? `url(#${gid})` : ACCENT}
              stroke={b.muted ? "rgba(87,215,226,0.4)" : "none"}
            />
            <text x={cx} y={BASE - bh - 6} fontSize={10} fontFamily={ff} fontWeight={700} fill={b.muted ? TICK : BRIGHT} textAnchor="middle">{b.label}</text>
          </g>
        );
      })}
    </>
  );
}

export default function CaseStudyChart({ slug, ariaLabel }: { slug: string; ariaLabel?: string }) {
  const c = CHARTS[slug];
  if (!c) return null;
  // Derived from the slug, not useId: these ids end up in prerendered HTML that
  // the browser then hydrates, and a counter-based id only matches if the page
  // was rendered from exactly the same starting state. The slug is the chart's
  // identity, so the id is the same in the static HTML and on the client.
  const gid = `csc-${slug}`;
  const topStop = c.type === "bars" ? 1 : 0.32;
  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      role="img"
      aria-label={ariaLabel || "Growth chart"}
      style={{ display: "block" }}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c.type === "bars" ? BRIGHT : ACCENT} stopOpacity={topStop} />
          <stop offset="1" stopColor={ACCENT} stopOpacity={c.type === "bars" ? 1 : 0} />
        </linearGradient>
      </defs>
      <line x1={L} y1={TOP + 24} x2={R} y2={TOP + 24} stroke={GRID} strokeWidth={0.5} />
      <line x1={L} y1={(TOP + BASE) / 2} x2={R} y2={(TOP + BASE) / 2} stroke={GRID} strokeWidth={0.5} />
      <line x1={L} y1={BASE} x2={R} y2={BASE} stroke={AXIS} strokeWidth={0.75} />
      {c.type === "line" && <LineChartBody c={c} gid={gid} />}
      {c.type === "twinline" && <TwinLineBody c={c} gid={gid} />}
      {c.type === "bars" && <BarsBody c={c} gid={gid} />}
    </svg>
  );
}
